'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { generateBlogKey } from '@eliadmin/core/src/utils/blog-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { BLOGS_CONFIG } from '@eliadmin/core/src/constants/blogs-config';
import React from 'react';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const [BlogCategoriesProvider, useBlogCategories] = createContextStore<
  { categories: BlogCategory[]; activeCategoryIds: string[] },
  { setActiveCategories: (categoryIds: string[]) => void }
>(
  {
    categories: [],
    activeCategoryIds: [],
  },
  (set, get) => ({
    setActiveCategories: (categoryIds: string[]) => set({ activeCategoryIds: categoryIds }),
  }),
);

export { BlogCategoriesProvider, useBlogCategories };

export type BlogCardsProps = {
  locale: string;
  query: FetchBlogsQuery;
  className?: {
    blog?: {
      main?: string;
      link?: string;
      image?: string;
      title?: string;
      date?: string;
      contentContainer?: string;
      content?: string;
    };
  };
};

export const BlogCards = ({ locale, query: queryProp, className }: BlogCardsProps) => {
  const { t } = useTranslations();

  const searchParams = useSearchParams();

  const search = searchParams.get('search');
  const activeCategories = useBlogCategories((s) => s.activeCategoryIds);

  const query = useMemo(
    () => ({
      where:
        !('filter' in queryProp) && activeCategories.length > 0
          ? { categories: { in: activeCategories } }
          : undefined,
      search: search ?? undefined,
      ...queryProp,
    }),
    [activeCategories, queryProp, search],
  );

  const fetchBlogsFn = async ({ pageParam = 1 }) => {
    const { data } = await fetchBlogs({
      locale,
      query: { ...query, page: pageParam } as any,
    });
    return data;
  };

  const {
    data: blogs,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery({
    initialPageParam: 0,
    queryKey: ['blogs', query],
    queryFn: fetchBlogsFn,
    getNextPageParam: (lastPage, pages) => pages.length + 1,
  });

  return (
    <div className="flex w-full flex-col">
      <div
        className={cn(
          'flex flex-wrap justify-center gap-4 md:justify-start',
          isFetching && 'opacity-30',
        )}
      >
        {blogs?.pages.map((p) => {
          return p.map((blog, i) => {
            if (i === 0 && query.search) return;
            return (
              <Card
                key={generateBlogKey(blog)}
                className={cn('relative w-[400px] max-w-[90vw] rounded-lg', 'ea-blog-card')}
              >
                <Link
                  href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
                  className={cn('ea-blog-card__link')}
                  prefetch={false}
                >
                  <CardContent
                    className={cn(
                      'rounded-lg border border-border p-4 shadow-md',
                      'ea-blog-card__content',
                    )}
                  >
                    <div className="flex h-[225px] w-full justify-center">
                      <Image
                        alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
                        src={blog.image?.url || '/images/placeholder.png'}
                        width="0"
                        height="0"
                        sizes="100vw"
                        className={cn('h-auto w-full rounded-lg', 'ea-blog-card__image')}
                      />
                    </div>
                    <div
                      className={cn('flex flex-col space-y-4 ', 'ea-blog-card__content-container')}
                    >
                      <Badge
                        size={'md'}
                        className={cn(
                          'pointer-events-none flex flex-grow-0 items-center gap-1 rounded-md bg-gray-100 px-2 text-xs font-semibold leading-5 text-foreground opacity-80',
                          'ea-blog-card__date-badge',
                          'absolute left-4 top-4',
                          className,
                        )}
                      >
                        {React.cloneElement(BLOGS_CONFIG.blogCard.calendarIcon as any, {
                          className: cn('ea-availability-label__icon'),
                        })}
                        {new Date(blog.created_at).toLocaleDateString(locale)}
                      </Badge>

                      <h3
                        data-ea-visual-tag="h6"
                        className={cn(
                          'mt-3 line-clamp-1 text-start font-bold',
                          'ea-blog-card__title',
                        )}
                      >
                        {blog.name}
                      </h3>
                      <Separator className="-my-2" />
                      <HtmlRendererRaw
                        as="p"
                        className={cn(
                          'mt-1 line-clamp-3 h-12 text-ellipsis text-sm leading-4 text-muted-foreground',
                          'ea-blog-card__content',
                        )}
                        html={getTextFromHTML(blog.content).replace(/&nbsp;/gm, '')}
                      />
                      <Button className="w-full rounded-lg bg-primary px-5 py-4 text-sm text-white  md:max-w-[150px]">
                        Čítať celý článok
                      </Button>
                    </div>
                  </CardContent>
                </Link>
              </Card>
            );
          });
        })}
      </div>
      {blogs?.pages.length === 1 && blogs?.pages?.every((page) => page.length === 0) && (
        <div className="flex w-full items-center justify-center">
          <p className="text-foreground">{t('text554')}</p>
        </div>
      )}
      <div className="flex justify-center">
        {blogs?.pages.length > 0 && blogs?.pages[blogs?.pages.length - 1].length > 0 && (
          <Button
            onClick={() => fetchNextPage()}
            variant={'outline'}
            disabled={isFetching}
            className=""
          >
            {t('text588')}
          </Button>
        )}
      </div>
    </div>
  );
};
